import type { ReactNode } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { chartAxis, chartTooltipBar } from '@/lib/chartTheme'
import { MONEY_SERIES, SeriesKey, useMoney, type MoneySeries } from './moneyUi'

export interface ResultPoint {
  /** Short label under the axis ("2 Sep"). */
  tick: string
  /** Full label in the tooltip ("Wed 2 Sep"). */
  label: string
  sales: number
  /** Omitted where the source has no cost of goods (the Overview's trend). */
  cogs?: number
  opex: number
  result: number
  /** Extra lines for the tooltip only — refunds, order count. */
  notes?: { label: string; value: string }[]
}

type Props = {
  title: string
  description: string
  points: ResultPoint[]
  /** What the blue column is ("Net sales", "Sales"). */
  salesLabel: string
  /** What the lower panel is ("Net profit", "Net profit after refunds"). */
  resultLabel: string
  /** A control for the header's right edge — the P&L's grain switch. */
  action?: ReactNode
  emptyText?: string
}

const SYNC = 'expense-period-result'
const Y_WIDTH = 56
const GRID = { stroke: 'hsl(var(--border))', vertical: false } as const

/**
 * Money in against money out, and what was left (F-FE-N164).
 *
 * Two panels on one time axis, each with its own honest scale: columns for
 * sales beside a column of costs, and under them the result, hanging below
 * zero on a loss. It replaces four splined lines — the curve invented dips
 * between days that had no data, and a rent payment and a Tuesday's sales are
 * amounts, not a trend.
 */
export function PeriodResultChart({ title, description, points, salesLabel, resultLabel, action, emptyText }: Props) {
  const money = useMoney()
  const hasCogs = points.some(p => p.cogs != null)
  const dense = points.length > 40

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 pb-2 sm:flex-row sm:items-start sm:justify-between sm:space-y-0">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="mt-1 max-w-prose text-xs leading-relaxed text-muted-foreground">{description}</p>
        </div>
        {action}
      </CardHeader>
      {points.length === 0 ? (
        <CardContent className="flex h-[160px] items-center justify-center text-sm text-muted-foreground">
          {emptyText ?? 'No completed sales or recorded expenses in this period.'}
        </CardContent>
      ) : (
        <CardContent className="space-y-1 pt-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pb-1" style={{ paddingLeft: Y_WIDTH }}>
            <SeriesKey series="sales">{salesLabel}</SeriesKey>
            {hasCogs && <SeriesKey series="cogs">Cost of goods</SeriesKey>}
            <SeriesKey series="opex">Operating expenses</SeriesKey>
          </div>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={points} syncId={SYNC} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={2} barCategoryGap={dense ? '10%' : '24%'}>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="tick" tick={false} axisLine={{ stroke: 'hsl(var(--border))' }} tickLine={false} height={6} />
                <YAxis {...chartAxis} width={Y_WIDTH} tickFormatter={v => money.axis(Number(v))} />
                <ReTooltip
                  {...chartTooltipBar}
                  isAnimationActive={false}
                  content={<ResultTooltip salesLabel={salesLabel} resultLabel={resultLabel} />}
                />
                <Bar dataKey="sales" name={salesLabel} fill={MONEY_SERIES.sales} maxBarSize={18} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                {hasCogs && (
                  <Bar dataKey="cogs" name="Cost of goods" stackId="costs" fill={MONEY_SERIES.cogs} maxBarSize={18} isAnimationActive={false}>
                    {/* The cap belongs to whichever segment ends the column. */}
                    {points.map(p => (
                      <Cell key={p.tick} radius={(p.opex > 0 ? 0 : [3, 3, 0, 0]) as unknown as number} />
                    ))}
                  </Bar>
                )}
                <Bar
                  dataKey="opex"
                  name="Operating expenses"
                  stackId="costs"
                  fill={MONEY_SERIES.opex}
                  stroke="hsl(var(--card))"
                  strokeWidth={hasCogs ? 1 : 0}
                  maxBarSize={18}
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pb-1 pt-3" style={{ paddingLeft: Y_WIDTH }}>
            <SeriesKey series="sales">{resultLabel}</SeriesKey>
            <SeriesKey series="loss">Loss</SeriesKey>
          </div>
          <div className="h-[170px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={points} syncId={SYNC} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap={dense ? '10%' : '24%'}>
                <CartesianGrid {...GRID} />
                <XAxis dataKey="tick" {...chartAxis} interval="preserveStartEnd" minTickGap={24} height={24} />
                <YAxis {...chartAxis} width={Y_WIDTH} tickFormatter={v => money.axis(Number(v))} />
                {/* Same hover band as the panel above; that panel's card carries the figures. */}
                <ReTooltip {...chartTooltipBar} isAnimationActive={false} content={() => null} />
                <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeOpacity={0.6} />
                <Bar dataKey="result" name={resultLabel} maxBarSize={18} isAnimationActive={false}>
                  {points.map(p => {
                    const loss = p.result < 0
                    return (
                      <Cell
                        key={p.tick}
                        fill={loss ? MONEY_SERIES.loss : MONEY_SERIES.sales}
                        radius={(loss ? [0, 0, 3, 3] : [3, 3, 0, 0]) as unknown as number}
                      />
                    )
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      )}
    </Card>
  )
}

function ResultTooltip({
  active,
  payload,
  salesLabel,
  resultLabel,
}: {
  active?: boolean
  payload?: { payload: ResultPoint }[]
  salesLabel: string
  resultLabel: string
}) {
  const money = useMoney()
  const p = payload?.[0]?.payload
  if (!active || !p) return null
  const rows: { series: MoneySeries; label: string; value: number }[] = [
    { series: 'sales', label: salesLabel, value: p.sales },
    ...(p.cogs != null ? [{ series: 'cogs' as const, label: 'Cost of goods', value: -p.cogs }] : []),
    { series: 'opex', label: 'Operating expenses', value: -p.opex },
  ]
  return (
    <div className="min-w-[220px] rounded-md border bg-popover px-3 py-2.5 text-xs text-popover-foreground shadow-md">
      <p className="mb-2 font-medium">{p.label}</p>
      <dl className="space-y-1">
        {rows.map(r => (
          <div key={r.label} className="flex items-center justify-between gap-6">
            <dt className="flex items-center gap-2 text-muted-foreground">
              <span aria-hidden className="h-0.5 w-3 rounded-full" style={{ background: MONEY_SERIES[r.series] }} />
              {r.label}
            </dt>
            <dd className="font-medium tabular-nums">{money.full(r.value)}</dd>
          </div>
        ))}
        <div className="flex items-center justify-between gap-6 border-t pt-1.5">
          <dt className="flex items-center gap-2 font-medium">
            <span
              aria-hidden
              className="h-0.5 w-3 rounded-full"
              style={{ background: p.result < 0 ? MONEY_SERIES.loss : MONEY_SERIES.sales }}
            />
            {resultLabel}
          </dt>
          <dd className={p.result < 0 ? 'font-semibold tabular-nums text-danger-fg' : 'font-semibold tabular-nums'}>
            {money.full(p.result)}
          </dd>
        </div>
      </dl>
      {p.notes && p.notes.length > 0 && (
        <dl className="mt-2 space-y-1 border-t pt-2 text-muted-foreground">
          {p.notes.map(n => (
            <div key={n.label} className="flex items-center justify-between gap-6">
              <dt>{n.label}</dt>
              <dd className="tabular-nums">{n.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
